import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiJson } from "../lib/api/_http";
import { ApiClientError } from "../lib/api/_errors";
import type { IdeaDirection } from "../lib/types";

// BacktestCard — score-an-idea preview. Lives on the propose form (debounced
// as the user types symbol/direction/allocation) and on the /upgrade hero.
//
// Two render modes off the same fetch:
//   - paid: full Sharpe / Sortino / Max Drawdown / Volatility / Total Return
//   - free: only Total Return, with everything else blurred + an Upgrade CTA
//
// The redacted flag on the wire decides which mode to render. Free callers
// still get a real number for Total Return so the teaser feels concrete; the
// blur applies to the visceral risk-adjusted metrics that paid users unlock.

interface BacktestCardProps {
	symbol: string;
	direction: IdeaDirection;
	allocationPct: number;
	/** Skip the debounce + fetch entirely. Useful for the upgrade-page static demo. */
	staticPayload?: BacktestPayload;
	/** Tweak: surface the "Run another" CTA. Default false on the propose page
	 *  since the form itself is the input. */
	showRunAgain?: boolean;
}

interface BacktestPayload {
	totalReturnPct: number;
	sharpe?: number;
	sortino?: number;
	maxDrawdownPct?: number;
	volatilityPct?: number;
	windowDays?: number;
	startDate?: string;
	endDate?: string;
	barsCount?: number;
	redacted?: boolean;
}

const DEBOUNCE_MS = 500;
const MIN_SYMBOL_LEN = 1;
const MIN_ALLOC = 0.25;

export function BacktestCard({
	symbol,
	direction,
	allocationPct,
	staticPayload,
	showRunAgain = false,
}: BacktestCardProps) {
	const [data, setData] = useState<BacktestPayload | null>(staticPayload ?? null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Tracks the inflight request so a faster keystroke can supersede a slow
	// upstream response (no flashing of stale data after a new query starts).
	const requestSeq = useRef(0);

	useEffect(() => {
		if (staticPayload) {
			setData(staticPayload);
			return;
		}
		const cleaned = symbol.trim().toUpperCase();
		if (
			cleaned.length < MIN_SYMBOL_LEN ||
			allocationPct < MIN_ALLOC ||
			allocationPct > 100
		) {
			setData(null);
			setError(null);
			return;
		}
		const seq = ++requestSeq.current;
		const timer = setTimeout(() => {
			setLoading(true);
			setError(null);
			apiJson<BacktestPayload>("/api/backtest", {
				method: "POST",
				body: JSON.stringify({
					symbol: cleaned,
					direction,
					allocationPct,
				}),
			})
				.then((res) => {
					if (seq !== requestSeq.current) return; // superseded
					setData(res);
				})
				.catch((err: unknown) => {
					if (seq !== requestSeq.current) return;
					if (err instanceof ApiClientError) {
						setError(humanizeError(err));
					} else {
						setError("Could not run backtest.");
					}
					setData(null);
				})
				.finally(() => {
					if (seq !== requestSeq.current) return;
					setLoading(false);
				});
		}, DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [symbol, direction, allocationPct, staticPayload]);

	return (
		<div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-sm font-semibold">5-year backtest</h3>
					<p className="text-[11px] text-[var(--color-muted)]">
						{data?.startDate && data?.endDate
							? `${data.startDate} → ${data.endDate}`
							: "Live preview of the proposed trade against the last ~5 years."}
					</p>
				</div>
				{showRunAgain && (
					<button
						type="button"
						onClick={() => {
							// Bump the seq so the next render re-runs the effect.
							requestSeq.current++;
							setData(null);
						}}
						className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:border-[var(--color-accent)]"
					>
						Run another
					</button>
				)}
			</div>

			<div className="mt-3">
				{loading && !data && (
					<p className="text-xs text-[var(--color-muted)]">Backtesting…</p>
				)}
				{error && (
					<p className="rounded-md border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 px-3 py-2 text-xs text-[var(--color-danger)]">
						{error}
					</p>
				)}
				{!loading && !error && !data && (
					<p className="text-xs text-[var(--color-muted)]">
						Enter a symbol + allocation to see the backtest.
					</p>
				)}
				{data && <BacktestMetrics data={data} />}
			</div>
		</div>
	);
}

function BacktestMetrics({ data }: { data: BacktestPayload }) {
	const isRedacted = data.redacted === true || data.sharpe === undefined;
	const ret = formatPct(data.totalReturnPct);
	const retClass =
		data.totalReturnPct >= 0 ? "text-emerald-400" : "text-[var(--color-danger)]";

	return (
		<div className="relative">
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
				<Metric label="Total return" value={ret} valueClass={retClass} />
				<Metric
					label="Sharpe"
					value={isRedacted ? "—" : formatNumber(data.sharpe!)}
					blurred={isRedacted}
				/>
				<Metric
					label="Sortino"
					value={isRedacted ? "—" : formatNumber(data.sortino!)}
					blurred={isRedacted}
				/>
				<Metric
					label="Max drawdown"
					value={isRedacted ? "—" : formatPct(-Math.abs(data.maxDrawdownPct!))}
					blurred={isRedacted}
				/>
				<Metric
					label="Volatility"
					value={isRedacted ? "—" : formatPct(data.volatilityPct!)}
					blurred={isRedacted}
				/>
			</div>

			{isRedacted && (
				<div className="absolute inset-0 flex items-end justify-center bg-gradient-to-b from-transparent via-[var(--color-bg-card)]/40 to-[var(--color-bg-card)] pt-8">
					<Link
						to="/upgrade"
						className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-xs font-semibold text-black transition hover:bg-emerald-400"
					>
						Upgrade to see Sharpe / Sortino / Drawdown
					</Link>
				</div>
			)}
		</div>
	);
}

interface MetricProps {
	label: string;
	value: string;
	valueClass?: string;
	blurred?: boolean;
}

function Metric({ label, value, valueClass, blurred }: MetricProps) {
	return (
		<div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-2">
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-muted)]">
				{label}
			</div>
			<div
				className={[
					"mt-1 text-sm font-semibold tabular-nums",
					valueClass ?? "",
					blurred ? "blur-[6px] select-none" : "",
				].join(" ")}
				aria-hidden={blurred ? true : undefined}
			>
				{value}
			</div>
		</div>
	);
}

function formatPct(n: number): string {
	const sign = n > 0 ? "+" : "";
	return `${sign}${n.toFixed(1)}%`;
}

function formatNumber(n: number): string {
	return n.toFixed(2);
}

function humanizeError(err: ApiClientError): string {
	switch (err.code) {
		case "invalid_request":
			return err.message || "Invalid backtest input.";
		case "not_found":
			return "We couldn't find enough price history for that symbol.";
		case "rate_limited":
			return "Backtest rate-limited — try again in a moment.";
		case "upstream_error":
		case "upstream_unavailable":
			return "Market data is temporarily unavailable.";
		default:
			return err.message || "Backtest failed.";
	}
}
